import { memo, useCallback, useEffect, useRef, useState } from 'react'

import { VideoStream } from '@nativewaves/nw-cloud-sdk/content'
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Table as ReactTableType, RowSelectionState, Row } from '@tanstack/react-table'
import { pick } from 'lodash-es'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { SearchBar } from '@shared/components/SearchBar'
import { SelectionToolbar, Table } from '@shared/components/Table'
import { ActionStrip, BasicListOperation } from '@shared/layouts'
import {
  NewDialog,
  RowActions,
  useColumns,
  SelectionToolbarActions,
  LifecyclePolicyDialog,
} from 'domains/Workspaces/Video/Streams'
import { BodyCellLink } from 'layouts/BodyCellLink'
import { videoStreamsOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/Video/Streams'
import { videoStreamGeneralRoute } from 'pages/NWPlatform/Workspaces/Workspace/Video/Streams/Stream'
import { videoStreamQueryKeys, workspaceQueryKeys } from 'services/queryKeys'

const OverviewRaw = () => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const queryClient = useQueryClient()

  const [newDialogOpen, setNewDialogOpen] = useState(false)
  const [streamData, setStreamData] = useState<VideoStream | VideoStream[] | undefined>(undefined)

  const tableInstanceRef = useRef<ReactTableType<VideoStream>>(undefined)

  const navigate = useNavigate({ from: videoStreamsOverviewRoute.fullPath })
  const { workspaceId } = videoStreamsOverviewRoute.useParams()
  const { query, groups, excludeGrouped } = videoStreamsOverviewRoute.useSearch()

  const workspaceQuery = useQuery(workspaceQueryKeys.detail({ workspaceId }))
  const { data: workspace } = workspaceQuery

  const videoStreamsQuery = useInfiniteQuery({
    ...videoStreamQueryKeys.list({
      repositoryId: workspace?.contentRepositoryId as string,
      query,
      groups,
      excludeGrouped,
    }),
    enabled: workspaceQuery.isSuccess && !!workspaceQuery.data.contentRepositoryId,
    select: (data) => ({
      flat: data.pages.flatMap((page) => page.items),
    }),
  })
  const videoStreams = videoStreamsQuery.data?.flat

  const columns = useColumns()

  const handleLifecyclePolicyClick = useCallback((selectedRows: Row<VideoStream>[]) => {
    const streamData = selectedRows.map((row) => row.original)
    setStreamData(streamData)
  }, [])

  useEffect(() => {
    if (videoStreamsQuery.isSuccess) {
      tableInstanceRef.current?.setRowSelection((old) =>
        Object.keys(old).reduce((acc, newItem) => {
          const newItemFound = videoStreams?.find((stream) => stream.id === newItem)
          if (newItemFound) {
            return { ...acc, [newItem]: true }
          }
          return acc
        }, {} as RowSelectionState),
      )
    }
  }, [videoStreams, videoStreamsQuery.isSuccess])

  return (
    <>
      <ActionStrip>
        <SearchBar
          placeholder={t('domain:Workspace.Video.searchVideoStream')}
          onSearch={({ search }) => navigate({ search: (prev) => ({ ...prev, query: search }) })}
          defaultValue={query}
        />

        <BasicListOperation
          refreshAction={useCallback(
            () =>
              queryClient.invalidateQueries({
                queryKey: videoStreamQueryKeys.list({
                  repositoryId: workspace?.contentRepositoryId,
                  query,
                  groups,
                  excludeGrouped,
                }).queryKey,
              }),
            [excludeGrouped, groups, query, queryClient, workspace?.contentRepositoryId],
          )}
          createAction={useCallback(() => {
            setNewDialogOpen(true)
            tableInstanceRef.current?.resetRowSelection()
          }, [setNewDialogOpen])}
        />
      </ActionStrip>

      <Table
        getTableInstance={(tableInstance) => void (tableInstanceRef.current = tableInstance)}
        size={48}
        data={videoStreams ?? []}
        columns={columns}
        isInitialLoadingGlobal={workspaceQuery.isLoading || videoStreamsQuery.isLoading}
        enableLinking
        LinkProps={(row, content, cell) => (
          <BodyCellLink
            sx={[!!cell.column.columnDef.meta?.align && { justifyContent: cell.column.columnDef.meta.align }]}
            from={videoStreamsOverviewRoute.fullPath}
            to={videoStreamGeneralRoute.to}
            params={{ videostreamId: row.original.id }}
          >
            {content}
          </BodyCellLink>
        )}
        excludeFieldsFromLinking={['id', 'contentRepositoryId']}
        enableInfinity
        infinityProps={pick(videoStreamsQuery, ['hasNextPage', 'isFetchingNextPage', 'fetchNextPage'])}
        ActionMenuProps={(row) => ({
          title: row.original.name,
          entity: t('entity:content.videoStream', { count: 1 }),
        })}
        RowActions={(props) => <RowActions onClick={(row) => setStreamData(row.original)} {...props} />}
        renderSelectionToolbar={({ selectedRows }) => (
          <SelectionToolbar
            selectedRows={selectedRows}
            title={t('entity:content.videoStream', { count: 2 })}
            discardSelection={() => tableInstanceRef.current?.resetRowSelection()}
            Actions={
              tableInstanceRef.current && (
                <SelectionToolbarActions
                  onLifecyclePolicyClick={() => handleLifecyclePolicyClick(selectedRows)}
                  selectedRows={selectedRows}
                  tableInstance={tableInstanceRef.current}
                />
              )
            }
          />
        )}
      />

      <Dialog open={newDialogOpen} onClose={() => setNewDialogOpen(false)}>
        <NewDialog />
      </Dialog>

      <Dialog open={!!streamData} onClose={() => setStreamData(undefined)}>
        {streamData ? (
          <LifecyclePolicyDialog
            streamData={streamData}
            onSuccess={() => tableInstanceRef.current?.resetRowSelection()}
          />
        ) : null}
      </Dialog>
    </>
  )
}

export const Overview = memo(OverviewRaw)
